import { observer } from 'mobx-react-lite'
import { useId, useState } from 'react'
import { NotificationsView } from './NotificationsView'
import classNames from 'classnames'
import { NavItem } from 'pages/layout/NavLink'
import { NumberBubble } from 'components/NumberBubble'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { BellIcon } from 'components/icons/Bell'
import { useNavigationCubit } from 'hooks/cubits/navigation'
import { Popover } from 'radix-ui'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'

export const NotificationsButton = observer(function NotificationsButton({
  className,
  containerClassName,
}: {
  className?: string
  containerClassName?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const user = useBreakoutUser()
  const { tt } = useTranslationTyped()
  const cubit = useNavigationCubit()
  const headingId = useId()

  /* We use forceDisableTooltip and render our own tooltip here because
     asChild does not play nice with BreakoutTooltip as a child, due to it
     having a Fragment as the main returned element. */
  const { isMobile, isClosed: isClosedNavigation } = useNavigationCubit()
  const shouldShowTooltip = isClosedNavigation && !isMobile

  const notificationCount = user.notifications.unreadCount

  return (
    <BreakoutTooltip
      ariaHidden
      enabled={shouldShowTooltip}
      content={tt.notifications.notifications()}
      placement="right"
    >
      <li className={classNames('relative', className)}>
        <Popover.Root modal open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <NavItem
              forceDisableTooltip
              data-testid="notifications-button"
              Icon={BellIcon}
              label={tt.notifications.notifications()}
              // Radix's Popover.Trigger injects aria-expanded and aria-controls
              // onto the trigger. The audit (Allyant 380) flagged these as
              // unnecessary here, so override them to undefined; Slot lets the
              // child prop win, which drops the attributes from the DOM.
              aria-expanded={undefined}
              aria-controls={undefined}
            />
          </Popover.Trigger>
          {notificationCount > 0 && (
            <NumberBubble
              count={notificationCount}
              srLabel={tt.notifications.unread_notifications()}
              className={classNames('absolute', {
                'left-[-3px] top-[-3px]':
                  cubit.isClosed && notificationCount >= 10,
                'left-[1px] top-[1px]':
                  cubit.isClosed && notificationCount < 10,
                'right-3 top-1/2 -translate-y-1/2': cubit.isOpen,
              })}
            />
          )}
          <Popover.Portal>
            <Popover.Content
              side="right"
              sideOffset={10}
              aria-labelledby={headingId}
              aria-modal
            >
              <NotificationsView
                className={containerClassName}
                onDismiss={() => setIsOpen(false)}
                headingId={headingId}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </li>
    </BreakoutTooltip>
  )
})
